/**
 * DocumentHeader - Shared header component for quotes and invoices
 * 
 * EF-QUOTE-STRUCTURE-V1: Redesigned for professional client-ready layout
 * - Client info on LEFT (logo, name, billing address, contact)
 * - Document info on RIGHT (document type, number, status)
 * - Tenant branding moved to footer
 */
import { 
  Phone, 
  Mail, 
  MapPin,
  User,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  AlertCircle,
  DollarSign
} from "lucide-react";
import { StatusBadge } from "../shared/DataComponents";

const statusIcons = {
  // Quote statuses
  draft: Clock,
  sent: FileText,
  approved: CheckCircle2,
  rejected: XCircle,
  // Invoice statuses
  paid: CheckCircle2,
  overdue: AlertCircle,
  cancelled: XCircle,
  partial: DollarSign,
};

const statusIconColors = {
  draft: "text-yellow-500",
  sent: "text-blue-500",
  approved: "text-green-500",
  rejected: "text-red-500",
  paid: "text-green-500",
  overdue: "text-red-500",
  cancelled: "text-muted-foreground",
  partial: "text-yellow-500",
};

export function DocumentHeader({ 
  documentType = "QUOTE", // "QUOTE" or "INVOICE"
  documentNumber,
  status,
  companyProfile,
  headerText,
  // EF-QUOTE-STRUCTURE-V1: Client info props
  client = null,
  clientContact = null,
}) {
  const StatusIcon = statusIcons[status] || FileText;
  const iconColor = statusIconColors[status] || "text-muted-foreground";

  return (
    <div className="border-b-2 border-border bg-white">
      {/* Main Header Row */}
      <div className="p-6 flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        
        {/* LEFT SIDE: Client Info */}
        <div className="flex-1">
          {/* Client Logo or Name */}
          {client?.logo_url ? (
            <img 
              src={client.logo_url} 
              alt={client.name || "Client Logo"}
              className="h-10 max-w-[160px] object-contain mb-2"
              data-testid="document-client-logo"
            />
          ) : (
            <h2 className="text-xl font-bold text-foreground" data-testid="document-client-name">
              {client?.name || "Client"}
            </h2>
          )}
          
          {/* Client Billing Address */}
          {client?.billing_address && (
            <div className="mt-2 text-sm text-muted-foreground flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="whitespace-pre-line">{client.billing_address}</div>
            </div>
          )}
          
          {/* Client Contact */}
          {clientContact && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                Contact
              </p>
              <div className="space-y-1">
                {clientContact.name && (
                  <p className="text-sm text-foreground flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    {clientContact.name}
                  </p>
                )}
                {clientContact.phone && (
                  <p className="text-sm text-foreground flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    {clientContact.phone}
                  </p>
                )}
                {clientContact.email && (
                  <p className="text-sm text-foreground flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    {clientContact.email}
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Fallback: Show client contact from client object */}
          {!clientContact && client?.contact_name && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                Contact
              </p>
              <div className="space-y-1">
                <p className="text-sm text-foreground flex items-center gap-2">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  {client.contact_name}
                </p>
                {client.contact_phone && (
                  <p className="text-sm text-foreground flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    {client.contact_phone}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT SIDE: Document Type & Number */}
        <div className="text-right flex-shrink-0">
          <div className="flex items-center justify-end gap-2">
            <StatusIcon className={`h-5 w-5 ${iconColor}`} />
            <span 
              className="text-2xl font-bold"
              style={{ color: companyProfile?.primary_color || '#0f172a' }}
            >
              {documentType}
            </span>
          </div>
          <p 
            className="text-xl font-mono mt-1" 
            style={{ color: companyProfile?.primary_color || '#0d9488' }} 
            data-testid="document-number"
          >
            {documentNumber}
          </p>
          <div className="mt-2">
            <StatusBadge status={status} />
          </div>
        </div>
      </div>
      
      {/* Header Text from Company Profile (Optional) */}
      {headerText && (
        <div className="px-6 pb-4 text-sm text-muted-foreground border-t border-border/50 pt-3">
          {headerText}
        </div>
      )}
    </div>
  );
}

/**
 * DocumentFooter - Tenant branding footer for quotes and invoices
 * 
 * EF-QUOTE-STRUCTURE-V1: Moved tenant info to footer
 * Shows: Tenant Name, Logo, Address, Bank Details
 */
export function DocumentFooter({ 
  companyProfile,
  showBankDetails = false 
}) {
  if (!companyProfile) return null;
  
  return (
    <div className="border-t-2 border-border bg-surface-elevated/30 p-6 mt-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        
        {/* Tenant Info */}
        <div className="flex items-start gap-4">
          {companyProfile.logo_url && (
            <img 
              src={companyProfile.logo_url} 
              alt={companyProfile.company_name || "Company Logo"}
              className="h-10 max-w-[120px] object-contain"
              data-testid="document-tenant-logo"
            />
          )}
          <div>
            <p className="font-semibold text-foreground">
              {companyProfile.company_name || "EventsFlo"}
            </p>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {companyProfile.address_line1 && (
                <p>{companyProfile.address_line1}{companyProfile.address_line2 ? `, ${companyProfile.address_line2}` : ""}</p>
              )}
              {(companyProfile.city || companyProfile.postcode) && (
                <p>{[companyProfile.city, companyProfile.postcode].filter(Boolean).join(", ")}</p>
              )}
              {companyProfile.country && <p>{companyProfile.country}</p>}
              {companyProfile.vat_number && (
                <p className="mt-1">{companyProfile.tax_label || "VAT"}: {companyProfile.vat_number}</p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="text-xs text-muted-foreground space-y-1">
          {companyProfile.contact_phone && (
            <p className="flex items-center gap-2">
              <Phone className="h-3 w-3" />
              {companyProfile.contact_phone}
            </p>
          )}
          {companyProfile.contact_email && (
            <p className="flex items-center gap-2">
              <Mail className="h-3 w-3" />
              {companyProfile.contact_email}
            </p>
          )}
        </div>

        {/* Bank Details (Optional) */}
        {showBankDetails && companyProfile.bank_details && (
          <div className="text-xs text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">Bank Details</p>
            <div className="whitespace-pre-line">{companyProfile.bank_details}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentHeader;
